/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useSetRecoilState } from "recoil";
import { useTranslation } from "react-i18next";
import "react-reflex/styles.css";

import getSchema from "../../lib/api/getSchema";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import AccessControl from "../functional/AccessControl";
import notificationState from "../../states/notificationState";
import DeleteRecordButton from "../input/DeleteRecordButton";
import CreateMaterialUnitGroupDetailButton from "../input/CreateMaterialUnitGroupDetailButton";
/**
 * @COMPONENT_NAME MaterialUnitGroupDetail
 * @DESCRIPTION 자재 단위 그룹 상세를 관리하는 화면 컴포넌트
 *             선택된 자재 단위 그룹에 대한 상세 정보를 표시하고 관리하는 기능 제공
 *
 * @PROPS
 *   - {Object} selectedMst - 선택된 자재 단위 그룹 정보
 *
 * @STATE
 *   - {Object} schema - 테이블 스키마 정보
 *   - {Object} grid - 그리드 객체 참조
 *   - {Object} api - API 객체 참조
 *   - {Object} selectDetail - 선택된 자재 단위 그룹 상세 정보
 *
 * @CALLBACKS
 *   - {Function} onInitialized - 컴포넌트 초기화 시 호출되는 콜백
 *   - {Function} onApiInitialized - API 초기화 시 호출되는 콜백
 *   - {Function} notifySuccess - 성공 알림을 표시하는 콜백
 *   - {Function} notifyFailure - 실패 알림을 표시하는 콜백
 *   - {Function} notifyWarning - 경고 알림을 표시하는 콜백
 *   - {Function} onSuccess - 작업 성공 시 호출되는 콜백
 *   - {Function} onFailure - 작업 실패 시 호출되는 콜백
 *   - {Function} onNotifyWarning - 경고 발생 시 호출되는 콜백
 */

const MaterialUnitGroupDetail = (props) => {
    const { selectedMst } = props;

    const table = "MaterialUnitGroupDetail";
    const view = "VwMaterialUnitGroupDetail";

    const [schema, setSchema] = useState();
    const [grid, setGrid] = useState();
    const [api, setApi] = useState();
    const [selectDetail, setSelectDetail] = useState();

    const { t } = useTranslation();
    const setNotification = useSetRecoilState(notificationState);

    useEffect(() => {
        let mounted = true;

        async function schemaSetting() {
            let schema = await getSchema(table);
            if (mounted) {
                setSchema(schema);
            }
        }

        schemaSetting();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (grid) {
            const handleGridSelection = (grid) => {
                setSelectDetail(grid.selectedItems?.length ? grid.selectedItems[0] : []);
            };

            grid.selectionChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });

            grid.itemsSourceChanged.addHandler((grid) => {
                handleGridSelection(grid);
            });
        }
    }, [grid]);

    useEffect(() => {
        if (api) {
            api.refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMst]);
    const onApiInitialized = (api) => {
        setApi(api);
    };

    const onInitialized = (grid) => {
        setGrid(grid);
    };

    const notifySuccess = (msg) => {
        setNotification({
            severity: "success",
            msg: msg,
            open: true,
        });
    };

    const notifyFailure = (msg) => {
        setNotification({
            severity: "error",
            msg: msg,
            open: true,
        });
    };

    const notifyWarning = (msg) => {
        setNotification({
            severity: "warning",
            msg: msg,
            open: true,
        });
    };

    const onSuccess = (msg) => {
        notifySuccess(msg);
        api?.refresh();
    };

    const onFailure = (reason) => {
        notifyFailure(reason);
    };

    const onNotifyWarning = (msg) => {
        notifyWarning(msg);
        api?.refresh();
    };

    return (
        <BasicCrudScreenType
            headerTitle={t("term.materialUnitGroupDetail")}
            id={table}
            table={table}
            view={view}
            onInitialized={onInitialized}
            onApiInitialized={onApiInitialized}
            onModifyViewUrl={(url) => `${url}/${selectedMst?.unitGroupId}`}
            noCreate
            noEdit
            noDelete
            noExcel
            onAppendButtonInFrontOfSearch={() => (
                <>
                    <BasicCrudScreenActionItemLayout>
                        <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                            <CreateMaterialUnitGroupDetailButton
                                id={table}
                                schema={schema}
                                refresh={api}
                                selectedMst={selectedMst}
                                onSuccess={onSuccess}
                            />
                        </AccessControl>
                    </BasicCrudScreenActionItemLayout>
                    <BasicCrudScreenActionItemLayout>
                        <AccessControl accessTarget={schema && schema.name} accessMethod="POST">
                            <DeleteRecordButton
                                id={table}
                                schema={schema}
                                refresh={api}
                                grid={grid}
                                color="secondary"
                                disabled={selectedMst?.length === 0}
                                onSuccess={onSuccess}
                                onFailure={onFailure}
                                onPartialFailure={onNotifyWarning}
                            />
                        </AccessControl>
                    </BasicCrudScreenActionItemLayout>
                </>
            )}
        />
    );
};

export default MaterialUnitGroupDetail;
